import { useTranslation } from 'react-i18next'
import { Pressable, ScrollView, StyleProp, StyleSheet, View, ViewStyle } from 'react-native'
import { Button, Checkbox, Color, Font, Radius, Spacing, Text } from 'zens-storybook'

import { Panel } from '../layout'
import { Tooltip } from '../tooltip'
import { deriveChatTitle } from './constants'
import type { Chat } from './types'

interface ChatListProps {
  chats: Chat[]
  currentChatId: string | null
  isLoadingChats: boolean
  selectedChatIds: string[]
  isDeletingChats: boolean
  hoveredChatTitleId: string | null
  setHoveredChatTitleId: (value: string | null | ((current: string | null) => string | null)) => void
  onSelectChat: (chatId: string) => void
  onToggleChatSelection: (chatId: string) => void
  onSelectAllChats: () => void
  onClearSelection: () => void
  onBulkDelete: () => void
  style?: StyleProp<ViewStyle>
}

const ChatList = ({
  chats,
  currentChatId,
  isLoadingChats,
  selectedChatIds,
  isDeletingChats,
  hoveredChatTitleId,
  setHoveredChatTitleId,
  onSelectChat,
  onToggleChatSelection,
  onSelectAllChats,
  onClearSelection,
  onBulkDelete,
  style,
}: ChatListProps) => {
  const { t } = useTranslation()

  return (
    <Panel style={[styles.chatListPanel, style]}>
      <View style={styles.chatListContent}>
        <View style={styles.chatListHeader}>
          <Text level={4}>{t('Your chats')}</Text>
        </View>
        <ScrollView style={styles.chatListScroll}>
          {isLoadingChats ? (
            <View style={styles.loadingContainer}>
              <Text>{t('Loading...')}</Text>
            </View>
          ) : chats.length === 0 ? (
            <View style={styles.chatListEmpty}>
              <Text style={styles.chatListEmptyText}>{t('No conversations yet')}</Text>
            </View>
          ) : (
            chats.map((chat) => {
              const isSelected = selectedChatIds.includes(chat.chatId)
              const handlePress = () => onSelectChat(chat.chatId)

              return (
                <Pressable
                  key={chat.chatId}
                  style={[
                    styles.chatListItem,
                    (isSelected || currentChatId === chat.chatId) && styles.chatListItemSelected,
                  ]}
                  onPress={handlePress}
                >
                  <View style={styles.chatListItemRow}>
                    <Checkbox
                      style={styles.chatListCheckbox}
                      value={isSelected}
                      onValueChange={() => onToggleChatSelection(chat.chatId)}
                      accessibilityLabel={t('Select chat {{id}}', { id: chat.chatId })}
                    />
                    <View style={styles.chatListItemBody}>
                      <Tooltip
                        label={chat.title || `${t('Chat')} #${chat.chatId.slice(0, 8)}`}
                        visible={hoveredChatTitleId === chat.chatId}
                        showArrow={true}
                        placement="top"
                        truncateLabel={false}
                        style={styles.chatTitleTooltipWrapper}
                      >
                        <View
                          onPointerEnter={() => setHoveredChatTitleId(chat.chatId)}
                          onPointerLeave={() =>
                            setHoveredChatTitleId((current) => (current === chat.chatId ? null : current))
                          }
                        >
                          <Text style={styles.chatListItemTitle}>
                            {deriveChatTitle(chat.title) || `${t('Chat')} #${chat.chatId.slice(0, 8)}`}
                          </Text>
                        </View>
                      </Tooltip>
                      <Text style={styles.chatListItemDate}>
                        {chat.updatedAt.toLocaleDateString()} - {chat.messageCount} {t('messages')}
                      </Text>
                    </View>
                  </View>
                </Pressable>
              )
            })
          )}
        </ScrollView>
        <View style={styles.bulkActionsBar}>
            <View style={styles.bulkActionsLeft}>
              <Checkbox
                value={chats.length > 0 && selectedChatIds.length === chats.length}
                onValueChange={(value) => (value ? onSelectAllChats() : onClearSelection())}
              />
              <Text style={styles.bulkSummaryText}>
                {selectedChatIds.length > 0
                  ? t('{{count}} selected', { count: selectedChatIds.length })
                  : t('Select chats to delete')}
              </Text>
            </View>
            <View style={styles.bulkActionsRight}>
              <Button
                size="S"
                danger
                title={isDeletingChats ? t('Deleting...') : t('Delete')}
                onPress={onBulkDelete}
                disabled={selectedChatIds.length === 0 || isDeletingChats}
              />
            </View>
          </View>
      </View>
    </Panel>
  )
}

const styles = StyleSheet.create({
  chatListPanel: {
    width: 400,
    backgroundColor: Color.simplyWhite,
    marginTop: Spacing.S,
    borderRadius: Radius.M,
    borderWidth: 1,
    borderColor: Color.calmGrey,
    overflow: 'visible',
  },
  chatListContent: {
    flex: 1,
    position: 'relative',
    overflow: 'visible',
  },
  chatListHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: Spacing.S,
    borderBottomWidth: 1,
    borderBottomColor: Color.calmGrey,
    borderTopLeftRadius: Radius.M,
    borderTopRightRadius: Radius.M,
    backgroundColor: Color.graphicGrey,
    height: 65
  },
  chatListScroll: {
    flex: 1,
    paddingBottom: Spacing.XL * 2,
    overflow: 'visible',
  },
  chatListItem: {
    padding: Spacing.S,
    borderBottomWidth: 1,
    borderColor: Color.calmGrey,
    backgroundColor: Color.simplyWhite,
    flexDirection: 'row',
    alignItems: 'center',
    gap: Spacing.S,
    overflow: 'visible',
  },
  chatListItemSelected: {
    backgroundColor: Color.sleepingGrey,
  },
  chatListItemRow: {
    flexDirection: 'row',
    alignItems: 'center',
    flex: 1,
    gap: Spacing.S,
  },
  chatListCheckbox: {
    paddingVertical: Spacing.XS,
  },
  chatListItemBody: {
    flex: 1,
    overflow: 'visible',
  },
  chatTitleTooltipWrapper: {
    overflow: 'visible',
  },
  chatListItemTitle: {
    marginBottom: Spacing.XS,
    fontFamily: Font.light,
    fontSize: 16
  },
  chatListItemDate: {
    color: Color.graphiteGrey,
    fontSize: 12,
  },
  chatListEmpty: {
    padding: Spacing.M,
    alignItems: 'center',
  },
  chatListEmptyText: {
    color: Color.graphicGrey,
    textAlign: 'center',
  },
  bulkActionsBar: {
    position: 'absolute',
    left: 0,
    right: 0,
    bottom: 0,
    padding: Spacing.S,
    backgroundColor: Color.simplyWhite,
    borderTopWidth: 1,
    borderColor: Color.calmGrey,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: Spacing.S,
  },
  bulkActionsLeft: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: Spacing.S,
  },
  bulkActionsRight: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: Spacing.S,
  },
  bulkSummaryText: {
    fontSize: 14,
    color: Color.graphiteGrey,
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
})

export default ChatList
